import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiUsers, FiPlus, FiSearch, FiFilter, FiTrash2, FiEdit, FiEye,
  FiDownload, FiRefreshCw, FiCalendar, FiDollarSign, FiCreditCard, FiUser,
  FiMessageSquare, FiX
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage, CHART_COLORS
} from '../../styles/AdminComponents';

// Modal form specific styled components (keeping only the create user modal specific ones)
import styled from 'styled-components';

const ModalFormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    input[type="checkbox"] {
      width: auto;
    }
  }
`;

// Enhanced Mobile-responsive user card with proper labels
const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .user-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .user-info {
      flex: 1;
      min-width: 0;
      
      .user-id {
        font-size: 1.1rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.25rem;
        word-break: break-all;
      }
      
      .access-code {
        font-size: 0.8rem;
        font-family: 'Monaco', 'Menlo', monospace;
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        color: #6b7280;
        word-break: break-all;
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
    }
  }
  
  .user-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
      }
      
      .badge {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }
    }
  }
  
  .enhanced-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    
    .stat-item {
      text-align: center;
      
      .stat-label {
        font-size: 0.7rem;
        color: #6b7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .stat-value {
        font-size: 1.1rem;
        font-weight: 700;
        color: #111827;
      }
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// User Details Modal (ensure it's properly styled and functional)
const UserDetailsModal = styled(Modal)`
  .modal-content {
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const UserDetailSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
    
    .detail-item {
      .label {
        font-size: 0.8rem;
        color: #6b7280;
        font-weight: 500;
        margin-bottom: 0.25rem;
      }
      
      .value {
        color: #111827;
        font-weight: 500;
        word-break: break-all;
      }
      
      &.full-width {
        grid-column: 1 / -1;
      }
    }
  }
`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [userStats, setUserStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Preserve all existing newUser state logic
  const [newUser, setNewUser] = useState({
    username: '',
    plan_id: 'trial_14',
    trial_days: 14,
    manual_date: false,
    expiry_date: '',
    is_free: false,
    payment_method: '',
    payment_details: ''
  });

  // Preserve exact existing plans logic
  const plans = {
    trial_7: { days: 7, price: 0 },
    trial_14: { days: 14, price: 0 },
    trial_30: { days: 30, price: 0 },
    monthly: { days: 30, price: 15 },
    quarterly: { days: 90, price: 40 },
    lifetime: { days: 36500, price: 150 },
    manual: { days: 0, price: 0 }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => {
      const matchesSearch = !searchTerm || 
        user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.access_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.payment_id && user.payment_id.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && user.expires_at && new Date(user.expires_at) > new Date()) ||
        (statusFilter === 'expired' && (!user.expires_at || new Date(user.expires_at) <= new Date()));

      const matchesPlan = planFilter === 'all' || 
        (user.plan_id && user.plan_id.toLowerCase().includes(planFilter.toLowerCase()));

      return matchesSearch && matchesStatus && matchesPlan;
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter, planFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Main endpoint now always returns enhanced data
      const res = await axios.get('/api/admin/users', { headers });
      setUsers(res.data.users);
      
      if (res.data.summary) {
        setUserStats(res.data.summary);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
      toast.error('Failed to fetch users');
    }
  };

  // Preserve EXACT existing plan change logic
  const handlePlanChange = (e) => {
    const selectedPlan = e.target.value;
    const isManual = selectedPlan === 'manual';
    const isTrial = selectedPlan === 'trial';
    
    let finalPlanId = selectedPlan;
    let expiryDate = new Date();
    
    if (isTrial) {
      // For trial, use the trial_days to create the final plan_id
      finalPlanId = `trial_${newUser.trial_days}`;
      expiryDate.setDate(expiryDate.getDate() + newUser.trial_days);
    } else if (!isManual && selectedPlan !== 'trial') {
      expiryDate.setDate(expiryDate.getDate() + plans[selectedPlan].days);
    }
    
    setNewUser({
      ...newUser,
      plan_id: isTrial ? finalPlanId : selectedPlan,
      manual_date: isManual,
      expiry_date: (isManual) ? newUser.expiry_date : expiryDate.toISOString() // Keep full timestamp
    });
  };

  // Preserve EXACT existing trial days change logic
  const handleTrialDaysChange = (e) => {
    const days = parseInt(e.target.value);
    const finalPlanId = `trial_${days}`;
    let expiryDate = new Date();
    // Add exact number of days (24 hours each) to preserve the time
    expiryDate.setDate(expiryDate.getDate() + days);
    
    setNewUser({
      ...newUser,
      trial_days: days,
      plan_id: finalPlanId,
      expiry_date: expiryDate.toISOString() // Keep full timestamp instead of just date
    });
  };

  // Preserve EXACT existing create user logic - CRITICAL: DO NOT LOSE THE PAYMENT CREATION
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const adminToken = localStorage.getItem('adminToken');
      const credentials = generateManualCredentials(newUser.plan_id);
      const user_id = newUser.username || credentials.user_id;
      const userResponse = await axios.post('/api/admin/users', {
        user_id,
        access_code: credentials.access_code,
        _action: 'manual_creation',
        expiry_date: newUser.expiry_date
      }, { headers: { Authorization: `Bearer ${adminToken}` } });

      if (userResponse.data.success) {
        // CRITICAL: Create payment record as well
        await axios.post('/api/admin/payments', {
          payment_id: `manual_${Date.now()}`,
          plan_id: newUser.plan_id,
          status: 'confirmed',
          amount: newUser.is_free ? 0 : plans[newUser.plan_id]?.price || 0,
          currency: 'usd',
          license_key: credentials.access_code,
          expires_at: newUser.expiry_date,
          pay_address: !newUser.is_free ? `${newUser.payment_method}: ${newUser.payment_details}` : 'Free License',
          _action: 'manual_creation'
        }, { headers: { Authorization: `Bearer ${adminToken}` } });

        setShowCreateUser(false);
        setNewUser({
          username: '',
          plan_id: 'trial_14',
          trial_days: 14,
          manual_date: false,
          expiry_date: '',
          is_free: false,
          payment_method: '',
          payment_details: ''
        });
        fetchUsers();
        toast.success('User created successfully!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to create user';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Preserve EXACT existing delete user logic
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.delete(`/api/admin/users/${userId}`, { headers });
      
      if (res.data.success) {
        fetchUsers();
        toast.success('User deleted successfully!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete user';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Updated manual credentials generation with proper User ID format
  const generateManualCredentials = (planId = '') => {
    const generateRandomString = (length, includeCase = false) => {
      const chars = includeCase 
        ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    // Check if it's a trial plan
    const isTrial = planId.includes('trial');
    
    const user_id = isTrial 
      ? `TRIAL_${generateRandomString(5)}` 
      : generateRandomString(10, true); // 10 chars with mixed case for regular users

    return {
      user_id,
      access_code: `ACCTW-MAN-${generateRandomString(16)}`
    };
  };

  const refreshUsers = () => {
    fetchUsers();
  };

  const exportUsers = () => {
    const csvContent = [
      ['User ID', 'Access Code', 'Plan', 'Status', 'Created', 'Payments', 'Revenue', 'Referrer'],
      ...filteredUsers.map(user => [
        user.user_id,
        user.access_code,
        user.plan_id || 'No Plan',
        user.expires_at && new Date(user.expires_at) > new Date() ? 'Active' : 'Expired',
        formatDate(user.created_at),
        user.payment_count || 0,
        user.total_revenue || 0,
        user.referrer || 'Direct'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
    setShowUserDetails(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getStatusBadge = (user) => {
    const isActive = user.expires_at && new Date(user.expires_at) > new Date();
    return {
      background: isActive ? '#dcfce7' : '#fee2e2',
      color: isActive ? '#166534' : '#dc2626',
      text: isActive ? 'Active' : 'Expired'
    };
  };

  const getPlanBadge = (planId) => {
    if (!planId) return { background: '#f3f4f6', color: '#6b7280', text: 'No Plan' };
    
    if (planId.includes('lifetime')) return { background: '#dcfce7', color: '#166534', text: planId };
    if (planId.includes('trial')) return { background: '#fef3c7', color: '#92400e', text: planId };
    return { background: '#dbeafe', color: '#1d4ed8', text: planId };
  };

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Users Management</h1>
              <p>Manage user accounts and permissions</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={() => setShowCreateUser(true)}>
                <FiPlus />
                <span className="btn-text">Create User</span>
              </ActionButton>
              <ActionButton onClick={refreshUsers} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportUsers} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Stats Section */}
          {userStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiUsers color="#3b82f6" /></StatIcon>
                  <h3>Total Users</h3>
                </StatHeader>
                <StatValue>{userStats.total_users}</StatValue>
                <StatLabel>Active Accounts</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiDollarSign color="#10b981" /></StatIcon>
                  <h3>Total Revenue</h3>
                </StatHeader>
                <StatValue>${userStats.total_revenue?.toFixed(2) || '0.00'}</StatValue>
                <StatLabel>From All Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiUsers color="#8b5cf6" /></StatIcon>
                  <h3>Active Users</h3>
                </StatHeader>
                <StatValue>{userStats.active_users || 0}</StatValue>
                <StatLabel>Currently Active</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiMessageSquare color="#f59e0b" /></StatIcon>
                  <h3>Users with Feedback</h3>
                </StatHeader>
                <StatValue>{userStats.users_with_feedback || 0}</StatValue>
                <StatLabel>Avg Age: {userStats.avg_account_age || 0} days</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </FilterSelect>
            <FilterSelect value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">All Plans</option>
              <option value="trial">Trial</option>
              <option value="monthly">Monthly</option>
              <option value="lifetime">Lifetime</option>
            </FilterSelect>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{filteredUsers.length} users found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Access Code</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Payments</th>
                      <th>Revenue</th>
                      <th>Referrer</th>
                      <th>Feedback</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => {
                      const statusBadge = getStatusBadge(user);
                      const planBadge = getPlanBadge(user.plan_id);
                      
                      return (
                        <tr key={user.user_id || index}>
                          <td>{user.user_id}</td>
                          <td>
                            <code style={{ fontSize: '0.8em', background: '#f3f4f6', padding: '2px 4px', borderRadius: '4px' }}>
                              {user.access_code}
                            </code>
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>{formatDate(user.last_login)}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: '500',
                              background: planBadge.background,
                              color: planBadge.color
                            }}>
                              {planBadge.text}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: '500',
                              background: statusBadge.background,
                              color: statusBadge.color
                            }}>
                              {statusBadge.text}
                            </span>
                          </td>
                          <td>{user.payment_count || 0}</td>
                          <td>{formatCurrency(user.total_revenue)}</td>
                          <td>
                            {user.referrer ? (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '0.8em',
                                background: '#dbeafe',
                                color: '#1d4ed8'
                              }}>
                                {user.referrer}
                              </span>
                            ) : '-'}
                          </td>
                          <td>{user.feedback_count || 0}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TableActionButton 
                                onClick={() => handleViewUser(user)}
                                title="View Details"
                              >
                                <FiEye />
                              </TableActionButton>
                              <TableActionButton 
                                onClick={() => handleDeleteUser(user.user_id)}
                                variant="danger"
                                title="Delete User"
                              >
                                <FiTrash2 />
                              </TableActionButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </div>

              {/* Enhanced Mobile Card View with proper labels */}
              <div className="mobile-view">
                {filteredUsers.map((user, index) => {
                  const statusBadge = getStatusBadge(user);
                  const planBadge = getPlanBadge(user.plan_id);
                  
                  return (
                    <UserCard key={user.user_id || index}>
                      <div className="user-header">
                        <div className="user-info">
                          <div className="user-id">{user.user_id}</div>
                          <div className="access-code">{user.access_code}</div>
                        </div>
                        <div className="actions">
                          <TableActionButton 
                            onClick={() => handleViewUser(user)}
                            title="View Details"
                          >
                            <FiEye />
                          </TableActionButton>
                          <TableActionButton 
                            onClick={() => handleDeleteUser(user.user_id)}
                            variant="danger"
                            title="Delete User"
                          >
                            <FiTrash2 />
                          </TableActionButton>
                        </div>
                      </div>
                      
                      <div className="user-details">
                        <div className="detail-item">
                          <div className="label">Created Date</div>
                          <div className="value">{formatDate(user.created_at)}</div>
                        </div>
                        <div className="detail-item">
                          <div className="label">Last Login</div>
                          <div className="value">{formatDate(user.last_login)}</div>
                        </div>
                        <div className="detail-item">
                          <div className="label">Plan</div>
                          <span className="badge" style={{
                            background: planBadge.background,
                            color: planBadge.color
                          }}>
                            {planBadge.text}
                          </span>
                        </div>
                        <div className="detail-item">
                          <div className="label">Status</div>
                          <span className="badge" style={{
                            background: statusBadge.background,
                            color: statusBadge.color
                          }}>
                            {statusBadge.text}
                          </span>
                        </div>
                      </div>
                      
                      <div className="enhanced-details">
                        <div className="stat-item">
                          <div className="stat-label">Payments</div>
                          <div className="stat-value">{user.payment_count || 0}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Revenue</div>
                          <div className="stat-value">{formatCurrency(user.total_revenue)}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Referrer</div>
                          <div className="stat-value">
                            {user.referrer ? (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '0.7em',
                                background: '#dbeafe',
                                color: '#1d4ed8'
                              }}>
                                {user.referrer}
                              </span>
                            ) : '-'}
                          </div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Feedback</div>
                          <div className="stat-value">{user.feedback_count || 0}</div>
                        </div>
                      </div>
                    </UserCard>
                  );
                })}
              </div>
            </ResponsiveContainer>
          )}

          {/* User Details Modal - Fixed and Enhanced */}
          {showUserDetails && selectedUser && (
            <UserDetailsModal>
              <ModalContent className="modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>User Details: {selectedUser.user_id}</h2>
                  <button 
                    onClick={closeUserDetails}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <UserDetailSection>
                  <h3>📋 Account Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">User ID</div>
                      <div className="value">{selectedUser.user_id}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Access Code</div>
                      <div className="value" style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {selectedUser.access_code}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Created</div>
                      <div className="value">{formatDate(selectedUser.created_at)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Last Login</div>
                      <div className="value">{formatDate(selectedUser.last_login)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Status</div>
                      <div className="value">
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.9em',
                          fontWeight: '500',
                          ...getStatusBadge(selectedUser)
                        }}>
                          {getStatusBadge(selectedUser).text}
                        </span>
                      </div>
                    </div>
                  </div>
                </UserDetailSection>

                <UserDetailSection>
                  <h3>💳 Payment Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Plan</div>
                      <div className="value">
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.9em',
                          fontWeight: '500',
                          ...getPlanBadge(selectedUser.plan_id)
                        }}>
                          {getPlanBadge(selectedUser.plan_id).text}
                        </span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Total Payments</div>
                      <div className="value">{selectedUser.payment_count || 0}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Total Revenue</div>
                      <div className="value" style={{ color: '#10b981', fontWeight: '600' }}>
                        {formatCurrency(selectedUser.total_revenue)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Expires At</div>
                      <div className="value">{formatDate(selectedUser.expires_at)}</div>
                    </div>
                  </div>
                </UserDetailSection>

                <UserDetailSection>
                  <h3>🔗 Referral Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Referrer</div>
                      <div className="value">
                        {selectedUser.referrer ? (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.9em',
                            fontWeight: '500',
                            background: '#dbeafe',
                            color: '#1d4ed8'
                          }}>
                            {selectedUser.referrer}
                          </span>
                        ) : 'Direct signup'}
                      </div>
                    </div>
                  </div>
                </UserDetailSection>

                <UserDetailSection>
                  <h3>📊 Activity Summary</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Feedback Submissions</div>
                      <div className="value">{selectedUser.feedback_count || 0}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Last Activity</div>
                      <div className="value">{formatDate(selectedUser.last_activity)}</div>
                    </div>
                  </div>
                </UserDetailSection>

                <ModalActions>
                  <ActionButton onClick={closeUserDetails} variant="secondary">
                    Close
                  </ActionButton>
                  <ActionButton 
                    onClick={() => {
                      closeUserDetails();
                      handleDeleteUser(selectedUser.user_id);
                    }} 
                    variant="danger"
                  >
                    <FiTrash2 />
                    Delete User
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </UserDetailsModal>
          )}

          {/* Create User Modal - PRESERVE ALL EXISTING FUNCTIONALITY */}
          {showCreateUser && (
            <Modal>
              <ModalContent>
                <h2>Create New User</h2>
                <form onSubmit={handleCreateUser}>
                  <ModalFormGroup>
                    <label>Username (Optional)</label>
                    <input
                      type="text"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      placeholder="Leave blank for auto-generated ID"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>Plan Type</label>
                    <select value={newUser.plan_id.includes('trial') ? 'trial' : newUser.plan_id} onChange={handlePlanChange}>
                      <option value="trial">Trial</option>
                      <option value="monthly">Monthly ($15)</option>
                      <option value="quarterly">Quarterly ($40)</option>
                      <option value="lifetime">Lifetime ($150)</option>
                      <option value="manual">Manual (Custom Date)</option>
                    </select>
                  </ModalFormGroup>

                  {newUser.plan_id.includes('trial') && (
                    <ModalFormGroup>
                      <label>Trial Days</label>
                      <select value={newUser.trial_days} onChange={handleTrialDaysChange}>
                        <option value={7}>7 Days</option>
                        <option value={14}>14 Days</option>
                        <option value={30}>30 Days</option>
                      </select>
                    </ModalFormGroup>
                  )}

                  {newUser.manual_date && (
                    <ModalFormGroup>
                      <label>Custom Expiry Date</label>
                      <input
                        type="datetime-local"
                        value={newUser.expiry_date ? new Date(newUser.expiry_date).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setNewUser({...newUser, expiry_date: new Date(e.target.value).toISOString()})}
                        required
                      />
                    </ModalFormGroup>
                  )}

                  <ModalFormGroup className="checkbox-group">
                    <input
                      type="checkbox"
                      checked={newUser.is_free}
                      onChange={(e) => setNewUser({...newUser, is_free: e.target.checked})}
                    />
                    <label>Mark as Free License</label>
                  </ModalFormGroup>

                  {!newUser.is_free && (
                    <>
                      <ModalFormGroup>
                        <label>Payment Method</label>
                        <select 
                          value={newUser.payment_method}
                          onChange={(e) => setNewUser({...newUser, payment_method: e.target.value})}
                          required
                        >
                          <option value="">Select Payment Method</option>
                          <option value="crypto">Cryptocurrency</option>
                          <option value="paypal">PayPal</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="other">Other</option>
                        </select>
                      </ModalFormGroup>

                      <ModalFormGroup>
                        <label>Payment Details</label>
                        <input
                          type="text"
                          value={newUser.payment_details}
                          onChange={(e) => setNewUser({...newUser, payment_details: e.target.value})}
                          placeholder="Transaction ID, address, etc."
                          required
                        />
                      </ModalFormGroup>
                    </>
                  )}

                  <ModalActions>
                    <ActionButton type="button" onClick={() => setShowCreateUser(false)} variant="secondary">
                      Cancel
                    </ActionButton>
                    <ActionButton type="submit">
                      <FiPlus />
                      Create User
                    </ActionButton>
                  </ModalActions>
                </form>
              </ModalContent>
            </Modal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default UsersPage; 